import format from 'date-fns/format';
import { config } from '@uhg-abyss/web/tools/config';
import pdfLogo from '@src/app/App/Content/Confirmation/print/pdf_logo.png';

// Add print styles to the document
const injectPrintStyles = () => {
  if (typeof window === 'undefined') return;

  const existingStyle = document.getElementById(
    'tin-confirmation-print-styles'
  );
  if (existingStyle) return; // Already injected

  const styleElement = document.createElement('style');
  styleElement.id = 'tin-confirmation-print-styles';
  styleElement.textContent = `
    @media print {
      /* Hide all elements except the print content */
      body * {
        visibility: hidden;
      }

      #print-confirmation,
      #print-confirmation *,
      #print-review,
      #print-review * {
        visibility: visible;
      }

      #print-confirmation,
      #print-review {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
      }

      /* Hide download buttons in print */
      #Pdf-downloadButton, [id*="downloadButton"], .step-indicator, #myaccount-button {
        display: none !important;
      }

      /* Clean up styling for print */
      * {
        box-shadow: none !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      /* Header styling */
      header, .abyss-card-title, [class*="abyss-card-title"], h3 {
        background-color: #E5E5E6 !important;
        padding: 10px !important;
        color: #323334 !important;
      }

      /* Card styling */
      [class*="abyss-card-section"] {
        padding: 12px 24px !important;
      }

      [class*="abyss-card-root"] {
        border: 1px solid #d1d5db !important;
      }

      [class*="abyss-checkbox-label"] {
        margin-left: 10px !important;
      }

      [class*="abyss-alert-container"] {
        border-color: #007000;
        background-color: #f2f8e6 !important;
      }

      [class*="abyss-alert-icon"] {
        color: #007000;
      }

      [class*="attestation-section"] {
        border-color: #d1d5db !important;
      }

      /* Page setup */
      @page {
        margin: 0.5in;
        size: letter;
      }

      /* Print header */
      .print-header {
        display: flex !important;
        justify-content: space-between;
        margin-bottom: 30px;
        padding-bottom: 15px;
      }

      .print-header-logo {
        max-width: 200px;
        height: auto;
      }

      .print-header-title {
        text-align: center;
        color: #002677;
        font-size: 20px;
        font-family: 'sans-serif';
        font-weight: bold;
      }

      .print-header-date {
        color: #002677;
        font-size: 18px;
        font-family: 'sans-serif';
        font-weight: bold;
        white-space: nowrap;
      }
    }

    @media screen {
      .print-header {
        display: none;
      }
    }
  `;
  document.head.appendChild(styleElement);
};

const createPrintHeader = async (title = 'TIN Registration') => {
  const header = document.createElement('div');
  header.className = 'print-header';

  // Logo
  const logo = document.createElement('img');
  logo.className = 'print-header-logo';
  const imgSrc =
    typeof pdfLogo === 'string' ? pdfLogo : pdfLogo?.src || pdfLogo?.default;

  try {
    // Convert image to data URL for better print compatibility
    let imgPath;
    if (config('APP_ENV') === 'local') {
      imgPath = imgSrc;
    } else {
      imgPath = `${config('PUBLIC_IMAGE')}${imgSrc}`;
    }

    const response = await fetch(imgPath);
    const blob = await response.blob();
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(blob);
    });
    logo.src = dataUrl;
  } catch (error) {
    // Fallback to direct path if fetch fails
    console.warn('Failed to load logo as data URL, using direct path:', error);
    if (config('APP_ENV') === 'local') {
      logo.src = imgSrc;
    } else {
      logo.src = `${config('PUBLIC_IMAGE')}${imgSrc}`;
    }
  }

  logo.alt = 'UnitedHealthcare Logo';

  // Title
  const titleDiv = document.createElement('div');
  titleDiv.className = 'print-header-title';
  titleDiv.textContent = title;

  // Date
  const date = document.createElement('div');
  date.className = 'print-header-date';
  date.textContent = format(new Date(), 'MM/dd/yyyy');

  header.appendChild(logo);
  header.appendChild(titleDiv);
  header.appendChild(date);

  return header;
};

export const print = {
  printPage: () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  },

  savePDF: async (element, npiInfo, fileName, callback) => {
    if (typeof window === 'undefined') {
      if (callback) callback();
      return;
    }

    // Inject print styles
    injectPrintStyles();

    // Add print header if it doesn't exist
    const confirmationElement = document.getElementById('print-confirmation');
    if (!confirmationElement) {
      if (callback) callback();
      return;
    }

    let printHeader = confirmationElement.querySelector('.print-header');
    if (!printHeader) {
      printHeader = await createPrintHeader('Tin Registration');
      confirmationElement.insertBefore(
        printHeader,
        confirmationElement.firstChild
      );
    }

    // Set document title for PDF filename suggestion
    const originalTitle = document.title;
    document.title = fileName;

    // Restore original title after print dialog closes
    const handleAfterPrint = () => {
      document.title = originalTitle;
      if (callback) callback();
    };
    window.addEventListener('afterprint', handleAfterPrint, { once: true });

    // Trigger print dialog
    window.print();
  },

  savePDFReview: async (element, info, fileName, callback) => {
    if (typeof window === 'undefined') {
      if (callback) callback();
      return;
    }

    // Inject print styles
    injectPrintStyles();

    // Hide edit forms temporarily for print
    const attestInputs = document.getElementById('attest-inputs');
    const locationsEdit = document.getElementById('locations-edit');
    const practiceEdit = document.getElementById('practice-edit');
    const w9Edit = document.getElementById('w9-edit');

    const backups = {
      attest: attestInputs?.style.display,
      locations: locationsEdit?.style.display,
      practice: practiceEdit?.style.display,
      w9: w9Edit?.style.display,
    };

    // Hide edit forms
    if (attestInputs) attestInputs.style.display = 'none';
    if (locationsEdit) locationsEdit.style.display = 'none';
    if (practiceEdit) practiceEdit.style.display = 'none';
    if (w9Edit) w9Edit.style.display = 'none';

    // Add print header if it doesn't exist
    const reviewElement = document.getElementById('print-review');
    if (!reviewElement) {
      // Restore displays
      if (attestInputs) attestInputs.style.display = backups.attest;
      if (locationsEdit) locationsEdit.style.display = backups.locations;
      if (practiceEdit) practiceEdit.style.display = backups.practice;
      if (w9Edit) w9Edit.style.display = backups.w9;
      if (callback) callback();
      return;
    }

    let printHeader = reviewElement.querySelector('.print-header');
    if (!printHeader) {
      printHeader = await createPrintHeader('Tin Registration');
      reviewElement.insertBefore(printHeader, reviewElement.firstChild);
    }

    // Insert the info (table element) after the attestation section if provided
    let infoInserted = false;
    if (info && info.nodeType === 1) {
      // Find the attestation section by id or class
      const attestationSection =
        reviewElement.querySelector('#attestation-data-review') ||
        reviewElement.querySelector('.attestation-section');
      if (attestationSection && attestationSection.parentNode) {
        // Insert after attestation section
        if (attestationSection.nextSibling) {
          attestationSection.parentNode.insertBefore(
            info,
            attestationSection.nextSibling
          );
        } else {
          attestationSection.parentNode.appendChild(info);
        }
        infoInserted = true;
      } else {
        // Fallback: append to reviewElement
        reviewElement.appendChild(info);
        infoInserted = true;
      }
    }

    // Set document title for PDF filename suggestion
    const originalTitle = document.title;
    document.title = fileName;

    // Restore original title and displays after print dialog closes
    const handleAfterPrint = () => {
      document.title = originalTitle;
      if (attestInputs) attestInputs.style.display = backups.attest;
      if (locationsEdit) locationsEdit.style.display = backups.locations;
      if (practiceEdit) practiceEdit.style.display = backups.practice;
      if (w9Edit) w9Edit.style.display = backups.w9;
      if (infoInserted && info && info.parentNode) {
        info.parentNode.removeChild(info);
      }
      if (callback) callback();
    };
    window.addEventListener('afterprint', handleAfterPrint, { once: true });

    // Trigger print dialog
    window.print();
  },
};
